import { useState, useCallback } from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { usePlayerStore } from '../store/player';
import { useBattleStore, type BattleKind } from '../store/battle';
import { content, displayName, useDisplayLocale } from '../content/bundle';
import { api } from '../api/client';
import { buildLineupFromMemberIds } from '../battle/buildLineup';

type RosterEntry = {
  memberId: string;
  level: number;
  xp: number;
  shards: number;
  equipped?: { passiveId: string; activeIds: string[]; ultimateId: string };
};

export function PreBattle() {
  const { t } = useTranslation();
  const { stageId, code } = useParams<{ stageId?: string; code?: string }>();
  const navigate = useNavigate();
  const me = usePlayerStore(s => s.me);
  const setBattleResult = useBattleStore(s => s.setBattleResult);
  const locale = useDisplayLocale();

  const [selected, setSelected] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const actualMode: BattleKind = stageId
    ? 'campaign'
    : code
    ? 'friend'
    : window.location.hash.includes('ladder')
    ? 'ladder'
    : 'daily';

  if (!me) return <Navigate to="/login" replace />;

  const roster = me.roster as RosterEntry[];
  const stage = stageId ? content.getStage(stageId) : undefined;

  const toggleMember = (memberId: string) => {
    setSelected(prev => {
      if (prev.includes(memberId)) return prev.filter(id => id !== memberId);
      if (prev.length >= 5) return prev;
      return [...prev, memberId];
    });
  };

  const synergyBonus = (() => {
    const groupIds = selected.map(id => content.getMember(id)?.groupId).filter(Boolean);
    const counts = groupIds.reduce<Record<string, number>>((acc, g) => {
      acc[g!] = (acc[g!] ?? 0) + 1;
      return acc;
    }, {});
    const maxSame = Math.max(0, ...Object.values(counts));
    if (maxSame >= 5) return '+25% Group Synergy';
    if (maxSame >= 3) return '+10% Group Synergy';
    return null;
  })();

  const isValid = selected.length === 5;

  const handleBattle = useCallback(async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError('');
    const requestId = crypto.randomUUID();
    const lineup = { memberIds: selected, requestId };

    // Build the full Lineup objects locally — server only returns MatchResult, not lineup data.
    // BattleScene needs slots with member + loadout + skills to render.
    let lineupA;
    try {
      lineupA = buildLineupFromMemberIds(selected, me!.playerId);
    } catch (e) {
      setError(String(e));
      setSubmitting(false);
      return;
    }

    try {
      if (actualMode === 'campaign' && stageId) {
        const res = await api.match.campaign({ stageId, lineup });
        const enemyIds = stage?.enemyLineup.map(e => e.memberId) ?? [];
        const lineupB = enemyIds.length === 5
          ? buildLineupFromMemberIds(enemyIds, 'campaign-enemy')
          : null;
        setBattleResult({
          matchId: res.matchId,
          kind: 'campaign',
          result: res.result,
          lineupA,
          lineupB
        });
      } else if (actualMode === 'daily') {
        const dateKey = new Date().toISOString().slice(0, 10);
        const res = await api.match.daily({ dateKey, lineup });
        // Daily enemies are server-generated procedurally — mirror the player's lineup
        // as a placeholder so BattleScene has something to render. The match RESULT
        // (event log) is the authoritative source of what happened.
        setBattleResult({
          matchId: res.matchId,
          kind: 'daily',
          result: res.result,
          lineupA,
          lineupB: buildLineupFromMemberIds(selected, 'daily-enemy'),
          themeName: res.themeName,
          attemptsLeft: res.attemptsLeft
        });
      } else if (actualMode === 'ladder') {
        const res = await api.match.ladder({ lineup });
        if (!res.matched) {
          setError(t('ladder.no_opponent'));
          setSubmitting(false);
          return;
        }
        setBattleResult({
          matchId: res.matchId,
          kind: 'ladder',
          result: res.result,
          lineupA,
          lineupB: buildLineupFromMemberIds(selected, 'ladder-enemy'),
          ladderDelta: res.ladderDelta,
          newTier: res.newTier,
          promoted: res.promoted
        });
      } else if (actualMode === 'friend' && code) {
        const res = await api.friend.match(code, { lineup });
        setBattleResult({
          matchId: res.matchId,
          kind: 'friend',
          result: res.result,
          lineupA,
          lineupB: buildLineupFromMemberIds(selected, 'friend-enemy')
        });
      }

      const { matchId } = useBattleStore.getState();
      if (matchId) navigate(`/battle/${matchId}`);
    } catch (e) {
      setError(String(e));
      setSubmitting(false);
    }
  }, [isValid, submitting, selected, actualMode, stageId, code, stage, setBattleResult, navigate]);

  const modeLabel: Record<BattleKind, string> = {
    campaign: stageId ? `Stage ${stageId}` : t('campaign.title'),
    daily: t('daily.title'),
    ladder: t('ladder.title'),
    friend: `Friend Match (${code ?? ''})`
  };

  return (
    <main className="min-h-screen flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <Link to={actualMode === 'campaign' ? '/campaign' : actualMode === 'ladder' ? '/ladder' : actualMode === 'daily' ? '/daily' : '/friends'}>
          <PixelButton variant="ghost" className="text-xs">{t('common.back')}</PixelButton>
        </Link>
        <PixelText size="xl" color="pink">{modeLabel[actualMode]}</PixelText>
      </div>

      {/* Enemy preview (campaign only) */}
      {stage && (
        <PixelPanel>
          <PixelText size="sm" color="yellow">{t('pre_battle.enemy_lineup')}</PixelText>
          <div className="flex gap-2 mt-2 flex-wrap">
            {stage.enemyLineup.map(enemy => {
              const m = content.getMember(enemy.memberId);
              const g = m ? content.getGroup(m.groupId) : undefined;
              return (
                <img
                  key={enemy.memberId}
                  src={`/stanwars/sprites/members/${enemy.memberId}/portrait.png`}
                  alt={m ? displayName(m, locale) : enemy.memberId}
                  title={m ? displayName(m, locale) : enemy.memberId}
                  className="w-8 h-8 border border-ink-600"
                  style={{ backgroundColor: g?.colorPrimary ?? '#444', imageRendering: 'pixelated', objectFit: 'contain' }}
                />
              );
            })}
          </div>
          {stage.boss && (
            <PixelText size="sm" color="pink" className="mt-1 block">{t('pre_battle.boss_warning')}</PixelText>
          )}
        </PixelPanel>
      )}

      {actualMode === 'ladder' && (
        <PixelPanel>
          <PixelText size="sm" color="cyan">{t('pre_battle.ladder_hint')}</PixelText>
        </PixelPanel>
      )}
      {actualMode === 'daily' && (
        <PixelPanel>
          <PixelText size="sm" color="yellow">{t('pre_battle.daily_hint')}</PixelText>
        </PixelPanel>
      )}

      {synergyBonus && (
        <PixelText size="sm" color="green">{synergyBonus}</PixelText>
      )}

      {/* Roster picker */}
      <PixelPanel>
        <PixelText size="base" color="pink" className="mb-2 block">
          {t('pre_battle.select_members', { selected: selected.length })}
        </PixelText>
        {roster.length === 0 ? (
          <PixelText size="sm" color="yellow">{t('pre_battle.no_members')}</PixelText>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {roster.map((entry: RosterEntry) => {
              const m = content.getMember(entry.memberId);
              const g = m ? content.getGroup(m.groupId) : undefined;
              const isSelected = selected.includes(entry.memberId);
              const name = m ? displayName(m, locale) : entry.memberId;
              return (
                <button
                  key={entry.memberId}
                  onClick={() => toggleMember(entry.memberId)}
                  className={`border-2 p-2 text-left transition-colors ${
                    isSelected ? 'border-neon-pink' : 'border-ink-600 hover:border-neon-cyan'
                  }`}
                  data-testid={`roster-member-${entry.memberId}`}
                >
                  <img
                    src={`/stanwars/sprites/members/${entry.memberId}/portrait.png`}
                    alt={name}
                    className="w-10 h-10 mb-1 block"
                    style={{ backgroundColor: g?.colorPrimary ?? '#888', imageRendering: 'pixelated', objectFit: 'contain' }}
                  />
                  <PixelText size="sm" color={isSelected ? 'pink' : 'white'}>
                    {name}
                  </PixelText>
                  {isSelected && (
                    <PixelText size="sm" color="cyan" className="block text-xs">{t('pre_battle.selected')}</PixelText>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </PixelPanel>

      {error && (
        <PixelText color="yellow">{error}</PixelText>
      )}

      <PixelButton
        onClick={handleBattle}
        disabled={!isValid || submitting}
        className="w-full"
      >
        {submitting ? t('pre_battle.submitting') : isValid ? t('pre_battle.battle') : t('pre_battle.need_more', { count: 5 - selected.length })}
      </PixelButton>
    </main>
  );
}
